/**
 * Client catalog for production-dark product doors.
 * Server env → getPublicProductDoors() on /api/auth/me and pricing.
 */

export const PRODUCT_DOOR_KEYS = Object.freeze([
  'bikeFit',
  'mobilityLibrary',
  'mobilityChat',
  'coachChat',
  'trainer',
  'bonkLab',
  'diyNutrition',
  'advancedAnalytics',
  'stravaOAuth',
  'zwiftOAuth',
  'records',
  'routes',
]);

export const SCREEN_TO_DOOR = Object.freeze({
  BikeFit: 'bikeFit',
  Mobility: 'mobilityLibrary',
  MobilityChat: 'mobilityChat',
  MobilityChatThread: 'mobilityChat',
  MobilityChatHistory: 'mobilityChat',
  Coach: 'coachChat',
  CoachThread: 'coachChat',
  TrainerSession: 'trainer',
  LiveRide: 'trainer',
  BonkLab: 'bonkLab',
  DIYNutrition: 'diyNutrition',
  PersonalBests: 'records',
  DistancePersonalBests: 'records',
  Routes: 'routes',
  RouteBriefing: 'routes',
});

export const HREF_TO_DOOR = Object.freeze({
  '/bike-fit': 'bikeFit',
  '/mobility': 'mobilityLibrary',
  '/coach': 'coachChat',
  '/trainer': 'trainer',
  '/bonk-lab': 'bonkLab',
  '/diy-nutrition': 'diyNutrition',
  '/personal-bests': 'records',
  '/routes': 'routes',
});

export function closedProductDoors() {
  const doors = {};
  for (const key of PRODUCT_DOOR_KEYS) {
    doors[key] = false;
  }
  return doors;
}

/**
 * @param {unknown} raw
 * @returns {Record<string, boolean>}
 */
export function normalizeProductDoors(raw) {
  const doors = closedProductDoors();
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return doors;
  }
  const rec = /** @type {Record<string, unknown>} */ (raw);
  for (const key of PRODUCT_DOOR_KEYS) {
    doors[key] = rec[key] === true;
  }
  return doors;
}

/**
 * @param {Record<string, boolean> | null | undefined} doors
 * @param {string} doorKey
 */
export function isProductDoorOpen(doors, doorKey) {
  if (!doorKey) return true;
  return doors?.[doorKey] === true;
}

/**
 * @param {string | null | undefined} screen
 * @param {string | null | undefined} href
 */
export function doorKeyForNavItem(screen, href) {
  if (screen && SCREEN_TO_DOOR[screen]) return SCREEN_TO_DOOR[screen];
  if (href && HREF_TO_DOOR[href]) return HREF_TO_DOOR[href];
  return null;
}

/**
 * Fail closed: items mapped to a door stay hidden until that door is true.
 * @template {{ screen?: string, href?: string, door?: string }} T
 * @param {ReadonlyArray<T>} items
 * @param {Record<string, boolean> | null | undefined} doors
 * @returns {T[]}
 */
export function filterNavItemsByDoors(items, doors) {
  const next = [];
  for (const item of items) {
    const door = item.door || doorKeyForNavItem(item.screen, item.href);
    if (!door || isProductDoorOpen(doors, door)) {
      next.push(item);
    }
  }
  return next;
}
